'use client';
import Image, { type StaticImageData } from 'next/image';
import Link from 'next/link';
import { dateToSimpleDate, getStartAndEndOfWeek, type GameMeta, type GameType } from '@puzzlio/core';
import { ALL_GAMES } from '@puzzlio/games';
import { Button } from '@puzzlio/ui';
import starsArt from '@puzzlio/ui/decorations/stars-card.png';
import fieldsArt from '@puzzlio/ui/decorations/fields-card.png';
import loopyRiverArt from '@puzzlio/ui/decorations/loopy-river-card.png';
import mosaicArt from '@puzzlio/ui/decorations/mosaic-card.png';
import { cn } from '@/app/lib/utils';
import { useUser } from '@/app/providers/UserProvider';
import { useIsInIntroMode } from '@/app/providers/ProgressProvider';
import { GAME_CONFIG } from '@/app/games/config';
import { useWeekStatusesForGame } from '@/app/hooks/gameBoardAndPlayHooks';
import { HomeWeekStrip } from './HomeWeekStrip';

/*
  Each game's fill, and where its art sits in the card. The pieces are different shapes, so each one's
  width and its distance from the card's right and bottom edges come from the Figma mockup.
  On a phone the art fills the column the text leaves free, between 78% and 100% of that width, so a
  narrow screen shrinks the art and never rewraps the text. Its negative left margin is how far it slips
  under the text.
  The "Ready when you are" section dresses itself as one of these games, so it reads the fill and art too.
*/
export const CARDS: Record<
  GameType,
  // `blurbClassName` narrows a description that would otherwise run into its art.
  { fill: string; stripFill: string; art: StaticImageData; artClassName: string; blurbClassName?: string }
> = {
  stars: {
    fill: 'bg-yellow',
    stripFill: 'bg-yellow-400',
    art: starsArt,
    artClassName: '-mr-1.5 -mb-1.5 -ml-7.5 max-w-35.75 min-w-28 md:right-5.5 md:bottom-2.5 md:w-37.5',
  },
  fields: {
    fill: 'bg-green-300',
    stripFill: 'bg-green-400',
    art: fieldsArt,
    artClassName: '-mr-3 -mb-2.5 -ml-9.75 max-w-39.5 min-w-30.75 md:right-4.5 md:bottom-3 md:w-41.5',
  },
  'loopy-river': {
    fill: 'bg-blue-200',
    stripFill: 'bg-blue-400',
    art: loopyRiverArt,
    artClassName:
      '-mr-3 mb-1 -ml-14.75 max-w-44.5 min-w-37.5 max-sm:-mr-9 max-sm:mb-4 md:right-2 md:bottom-6 md:w-44.5',
  },
  mosaic: {
    fill: 'bg-purple-300',
    stripFill: 'bg-purple-400',
    art: mosaicArt,
    artClassName: '-mr-1.75 -mb-5 -ml-2.5 max-w-35 min-w-30.5 max-sm:-mr-6.75 md:right-5.25 md:bottom-0 md:w-35',
    blurbClassName: 'max-w-48 md:max-w-44',
  },
};

/**
 * Where a card's Play button goes: the tutorial while the player is new to the game, then the week's
 * earliest puzzle they haven't finished, so a player who fell behind picks up where they left off. Today's
 * puzzle once the week is done, or while the player's plays are still loading.
 */
function usePlayURL(gameType: GameType, inIntroMode: boolean): string {
  const today = dateToSimpleDate(new Date());
  const { data } = useWeekStatusesForGame(gameType, getStartAndEndOfWeek(today)[0], today, {
    enabled: !inIntroMode,
  });
  if (inIntroMode) return GAME_CONFIG[gameType].tutorialURL;
  const day = data?.find((entry) => entry.playStatus !== 'complete')?.date ?? today;
  return GAME_CONFIG[gameType].gameURL(day);
}

function HomeGameCard({ game, showWeek }: { game: GameMeta; showWeek: boolean }) {
  const { fill, stripFill, art, artClassName, blurbClassName } = CARDS[game.type];
  const inIntroMode = useIsInIntroMode(game.type);
  const href = usePlayURL(game.type, inIntroMode);
  const comingSoon = game.availability === 'comingSoon';

  return (
    <div data-testid={`game-card-${game.type}`} className={cn('flex flex-col overflow-hidden md:rounded-xl', fill)}>
      <div className="relative grid min-h-[170px] flex-1 grid-cols-[auto_minmax(0,1fr)] grid-rows-[auto_auto_1fr] justify-items-start py-5 pr-5 pl-6 md:flex md:min-h-[176px] md:flex-col md:items-start md:p-5">
        <div
          className={cn(
            'pointer-events-none col-start-2 row-span-3 row-start-1 self-end justify-self-end md:absolute md:m-0 md:max-w-none md:min-w-0',
            artClassName
          )}
        >
          <Image src={art} alt="" priority className="block h-auto w-full" />
        </div>
        <h2 className="font-chinook relative col-start-1 text-3xl leading-none">{game.label}</h2>
        <p
          className={cn(
            'font-quicksand relative col-start-1 mt-0.5 mb-4 max-w-56 text-base leading-tight font-bold md:font-semibold',
            blurbClassName
          )}
        >
          {/* Backed with the card's fill, line by line, so art that runs under it can't cut through the words. */}
          <span className={cn('box-decoration-clone', fill)}>{game.description}</span>
        </p>
        {comingSoon ? (
          <Button size="card" disabled className="relative col-start-1 mt-auto">
            Coming Soon
          </Button>
        ) : (
          <Button asChild size="card" className="relative col-start-1 mt-auto">
            <Link prefetch={false} href={href}>
              Play {game.label}
            </Link>
          </Button>
        )}
      </div>
      {showWeek && !comingSoon && <HomeWeekStrip gameType={game.type} disabled={inIntroMode} className={stripFill} />}
    </div>
  );
}

/** The four games: full-bleed bands stacked on a phone, a two-by-two grid of cards from tablet up. */
export function HomeGameCards({ className }: { className?: string }) {
  // The week only means something to a signed-in player, so anyone else gets the plain cards.
  const signedIn = !!useUser()?.auth?.email;

  return (
    <div
      className={cn('grid w-full md:max-w-[776px] md:grid-cols-2 md:gap-3', className)}
      data-ph-capture-attribute-nav="home-game-cards"
    >
      {ALL_GAMES.map((game) => (
        <HomeGameCard key={game.type} game={game} showWeek={signedIn} />
      ))}
    </div>
  );
}
